import * as Sentry from '@sentry/vue'
import {AUTH_TOKEN_KEY, IMPERSONATION_QUERY_KEY} from '@shared/constants/auth'
import {getParamFromUrl} from '@shared-utils/url/getParamFromUrl'
import {redirectToExternalUrl} from '@shared-utils/url/redirectToExternalUrl'
import {AppUrls, oldDomains} from '@shared-utils/auth/appUrls'
import {removeUrlParams} from '@shared-utils/url/removeUrlParams'
import {
  middlewareAccessControlAndTitle,
  middlewareImpersonationAndAccessControlAndSetTitle,
} from '@shared-utils/router/middlewares/index'
import {getImpersonationUserToken} from '@shared-utils/auth/impersonation/helpers'
import {getHomeApp} from '@shared-utils/auth/homeAppCookie'

function cleanAuthParamsFromUrlRedirect(to) {
  if (!to.query?.code && !to.query?.redirect_url) {
    return null
  }

  const query = {...to.query}
  delete query.code
  delete query.redirect_url
  delete query.redirect_uri

  return {
    path: to.path,
    query,
    replace: true,
  }
}
function handleRedirectToClientOrCrm(type = 'client', path) {
  const origin = (type === 'client' ? AppUrls.client() : AppUrls.crm()).replace(/\/+$/, '')
  const currentOrigin = window.location.origin.replace(/\/+$/, '')

  const target = new URL(path, origin).toString()

  if (currentOrigin === origin) {
    return null
  }

  redirectToExternalUrl(target)

  return {external: true}
}
function redirectUserToHomeByCookie(path) {
  const homeApp = getHomeApp()
  const currentOrigin = window.location.origin
  const crmOrigin = AppUrls.crm().replace(/\/+$/, '')
  const clientOrigin = AppUrls.client().replace(/\/+$/, '')

  if (homeApp === 'crm' && currentOrigin !== crmOrigin) {
    return handleRedirectToClientOrCrm('crm', path)
  } else if (homeApp === 'app' && currentOrigin !== clientOrigin) {
    return handleRedirectToClientOrCrm('client', path)
  }

  return null
}
async function handleLoginAndGetToken(authenticationStore) {
  const authCode = getParamFromUrl('code')
  const redirectUrl =
    getParamFromUrl('redirect_uri') ||
    getParamFromUrl('redirect_url') ||
    `${window.location.origin}${window.location.pathname}`
  const authPayload = {
    grant_type: 'authorization_code',
    client_id: process.env.VUE_APP_OAUTH_CLIENT_ID,
    redirect_uri: redirectUrl,
    code: authCode,
  }

  try {
    await authenticationStore.loginAndSetToken(authPayload)
    const token = localStorage.getItem(AUTH_TOKEN_KEY)

    return {token: token}
  } catch (e) {
    console.error('handleLoginAndGetToken failed', e)
    Sentry.captureException(e, {
      tags: {auth_flow: 'login_code_exchange'},
      extra: {
        redirect_uri: redirectUrl,
        has_code: !!authCode,
        status: e?.response?.status,
        response_data: e?.response?.data,
      },
    })
    redirectToExternalUrl(AppUrls.login(), {
      redirect_url: removeUrlParams(redirectUrl),
    })
    return {external: true}
  }
}
function checkForAlias() {
  const current = new URL(window.location.href)

  if (!oldDomains.includes(current.hostname)) {
    return
  }

  const base = AppUrls.crm()

  const path = current.pathname === '/login' ? '' : current.pathname + current.search

  const target = new URL(path, base)

  redirectToExternalUrl(target.toString())

  return {external: true}
}

export async function mainMiddleware({...args}) {
  const {
    to,
    authenticationStore,
    defaultStore,
    router,
    accessRules,
    checkTwoFA = false,
    redirectClientsToApp = false,
    redirectEmployeesToCrm = false,
  } = args
  const auth = authenticationStore

  const hasAuthCallbackCode = !!to.query?.code
  if (hasAuthCallbackCode) {
    await auth.removeUserToken()
    auth.user = null
  }

  let token = localStorage.getItem(AUTH_TOKEN_KEY)
  const isImpersonation = !!to.query?.[IMPERSONATION_QUERY_KEY] || getImpersonationUserToken()
  const currentPath = `${window.location.pathname}${window.location.search}`

  try {
    checkForAlias()

    if (isImpersonation) {
      const impersonationRoute = await middlewareImpersonationAndAccessControlAndSetTitle({
        to,
        authenticationStore,
        router,
        accessRules,
      })

      if (impersonationRoute?.impersonated) {
        return null
      }
    }

    if (!token) {
      const homeRedirect = redirectUserToHomeByCookie(currentPath)
      if (homeRedirect) {
        return homeRedirect
      }

      await defaultStore.setFullPagePreloader(true)
      let result = await handleLoginAndGetToken(auth)

      if (!result?.token) {
        return result
      }

      token = result.token
    }

    if (!auth.isAuthenticated && token) {
      await defaultStore.setFullPagePreloader(true)
      await auth.loadAuthUserInfo()
    }

    if (redirectClientsToApp && auth?.user?.role === 'external' && token) {
      return handleRedirectToClientOrCrm('client', currentPath)
    }
    if (redirectEmployeesToCrm && auth?.user?.policies?.is_point2web_employee && token && !isImpersonation) {
      return handleRedirectToClientOrCrm('crm', currentPath)
    }

    if (checkTwoFA && !auth.hasTwoFA && auth.isPoint2WebEmployee && to.name !== 'two-fa') {
      return {name: 'two-fa', query: {redirect_url: currentPath}}
    }

    const accessRoute = middlewareAccessControlAndTitle({
      to,
      authenticationStore,
      accessRules,
    })

    return accessRoute || cleanAuthParamsFromUrlRedirect(to) || null
  } finally {
    await defaultStore.setFullPagePreloader(false)
  }
}
